import { useMemo } from "react";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import { useSelector } from "react-redux";
import { Box, Flex } from "rebass";
import styled from "styled-components";
import { Avatar } from "../../components";
import useResponsive from "../../hooks/useResponsive";
import { getLocalizedString, theme } from "../../utils";
import config from "../../utils/globalConfig";

const OrderDetailText = styled.strong`
  display: inline-block;
  margin-bottom: 15px;
`;

const OrderData = ({
  order,
  customer,
  showInfo,
  setShowInfo,
  actions = null,
}) => {
  const { isMobile, isTablet } = useResponsive();
  const { configuration } = useSelector((state) => state.yodaOrders);

  const lang = localStorage.getItem("B2BLanguage") == "ITA" ? "it" : "en";

  const availabilityColorsMap = {
    green: "#249d4e",
    yellow: "#cea418",
    red: "#f04836",
  };

  const colorStatus = useMemo(() => {
    switch (order?.status) {
      case "Draft":
        return availabilityColorsMap.yellow;
      case "Confirm":
        return availabilityColorsMap.green;
      default:
        return availabilityColorsMap.red;
    }
  }, [order?.status]);

  const orderColor =
    order?.status === "Draft"
      ? theme.draftColor
      : order?.status === "WaitingForConfirmation"
        ? theme.waitingColor
        : order?.status === "Confirmed"
          ? theme.confirmedColor
          : theme.cancelledColor;

  return (
    <>
      {/* ORDER HEADER */}
      {isMobile || isTablet ? (
        <Flex
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          p="10px"
          onClick={() => setShowInfo(!showInfo)}
        >
          <Avatar
            name={customer && customer.name}
            size={40}
            isPage
            nameSize="20px"
          />
          {showInfo ? (
            <BsChevronDown size="24px" />
          ) : (
            <BsChevronUp size="24px" />
          )}
        </Flex>
      ) : (
        <Box
          p={3}
          sx={{
            position: "relative",
            background: "white",
            width: "100%",
            maxWidth: config.VIEWPORT,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          <Flex
            width={1}
            alignItems="left"
            justifyContent={"space-between"}
            flexDirection={"row"}
          >
            <Avatar name={customer && customer.name} size={40} isPage pl={0} />
            <Flex
              alignItems="center"
              flexDirection={"row"}
              paddingRight={"4px"}
            >
              {actions}
            </Flex>
          </Flex>
        </Box>
      )}
      {/* ORDER INFORMATION */}
      {isMobile || isTablet ? (
        showInfo && (
          <Flex
            flexDirection={"column"}
            justifyContent="left"
            alignItems="flex-start"
            mb="20px"
            px="1.5rem"
            {...(isTablet && {
              flexDirection: "row",
              flexWrap: "wrap",
            })}
          >
            <Flex flexDirection="column" mb="20px" flex={"1 0 50%"}>
              <Box>
                <span style={{ marginRight: "8px" }}>
                  {getLocalizedString("Codice cliente")}:
                </span>
                <span style={{ fontWeight: "bold" }}>{customer?.code}</span>
              </Box>
              <Box>
                <span style={{ marginRight: "8px" }}>
                  {getLocalizedString("ORDER_BUSINESS_NAME")}:
                </span>
                <span style={{ fontWeight: "bold" }}>{customer?.name}</span>
              </Box>
            </Flex>
            <Flex flexDirection="column" mb="20px" flex={"1 0 50%"}>
              <Box>
                <span style={{ marginRight: "8px" }}>
                  {getLocalizedString("Tipo")}:
                </span>
                <span style={{ fontWeight: "bold" }}>
                  {configuration?.filter?.(
                    (f) => f.typeId == order?.orderTypeId,
                  )[0]?.description?.[lang] || ""}
                </span>
              </Box>
              <Box>
                <span style={{ marginRight: "8px" }}>
                  {getLocalizedString("Stagione")}:
                </span>
                <span style={{ fontWeight: "bold" }}>
                  {configuration
                    ?.filter?.((f) => f.typeId == order?.orderTypeId)?.[0]
                    ?.configuration?.filter(
                      (f) =>
                        f.seasonId == order?.seasonId &&
                        f.lineID == order?.customData?.lineId,
                    )[0]?.seasonDescription?.[lang] || ""}
                </span>
              </Box>
              <Box>
                <span style={{ marginRight: "8px" }}>
                  {getLocalizedString("Linea")}:
                </span>
                <span style={{ fontWeight: "bold" }}>
                  {configuration
                    ?.filter?.((f) => f.typeId == order?.orderTypeId)?.[0]
                    ?.configuration?.filter?.(
                      (f) =>
                        f.lineID == order?.customData?.lineId &&
                        f.seasonId == order?.seasonId,
                    )[0]?.lineDescription?.[lang] || ""}
                </span>
              </Box>
            </Flex>
            <Flex flexDirection="column" flex={"1 0 50%"}>
              <Box>
                <span style={{ marginRight: "8px" }}>
                  {getLocalizedString("Porta")}:
                </span>
                <span style={{ fontWeight: "bold" }}>
                  {customer?.doors.filter((f) => f.code == "M")[0].code} -{" "}
                  {customer?.doors.filter((f) => f.code == "M")[0].name}
                </span>
              </Box>
              <Box>
                <span style={{ marginRight: "8px" }}>
                  {getLocalizedString("ORDER_DATE")}:
                </span>
                <span style={{ fontWeight: "bold" }}>
                  {order?.customData?.orderDateFe || ""}
                </span>
              </Box>
              <Box sx={{ mb: "20px" }}>
                <span style={{ marginRight: "8px" }}>
                  {getLocalizedString("Stato")}:
                </span>
                <span style={{ fontWeight: "bold", color: colorStatus }}>
                  {getLocalizedString("STATUS_" + order?.status?.toUpperCase())}
                </span>
              </Box>

              {order?.note?.it && (
                <Box>
                  <span style={{ marginRight: "8px" }}>
                    {getLocalizedString("ORDER_NOTES")}:
                  </span>
                  <span style={{ fontWeight: "bold" }}>
                    {order?.note?.it || ""}
                  </span>
                </Box>
              )}
            </Flex>
            {order?.customData?.ponumber && (
              <Flex flexDirection="column" mb="20px" flex={"1 0 50%"}>
                <Box>
                  <span style={{ marginRight: "8px" }}>
                    {getLocalizedString("PO")}
                    {": "}
                  </span>
                  <span style={{ fontWeight: "bold" }}>
                    {order?.customData?.ponumber || "-"}
                  </span>
                </Box>
              </Flex>
            )}
          </Flex>
        )
      ) : (
        <Box width={1} p={3}>
          <Box px={"4em"}>
            <Flex
              width={1}
              sx={{
                fontSize: "1em",
                color: "#232323",
              }}
              style={{
                position: "relative",
              }}
              flexDirection={["column", "row", "row"]}
              flexWrap={"wrap"}
              justifyContent={"space-between"}
              alignContent={"center"}
            >
              <Flex
                flexDirection="column"
                flex="1 0 calc(100% / 4 - 100px)"
                style={{ display: "grid" }}
              >
                <Box>
                  {getLocalizedString("Ordine N°")}:{" "}
                  <OrderDetailText>{order?.orderNumber}</OrderDetailText>
                </Box>
                <Box>
                  {getLocalizedString("Codice cliente")}:{" "}
                  <OrderDetailText>{customer?.code}</OrderDetailText>
                </Box>
                <Box>
                  {getLocalizedString("ORDER_BUSINESS_NAME")}:{" "}
                  <OrderDetailText>{customer?.name}</OrderDetailText>
                </Box>
              </Flex>
              <Flex
                minWidth={"20px"}
                flexDirection="column"
                flex="1 0 calc(100% / 4 - 100px)"
                style={{ display: "grid" }}
              >
                <Box>
                  {getLocalizedString("Tipo")}:{" "}
                  <OrderDetailText>
                    {configuration?.filter?.(
                      (f) => f.typeId == order?.orderTypeId,
                    )[0]?.description?.[lang] || ""}
                  </OrderDetailText>
                </Box>
                <Box>
                  {getLocalizedString("Stagione")}:{" "}
                  <OrderDetailText>
                    {configuration
                      ?.filter?.((f) => f.typeId == order?.orderTypeId)?.[0]
                      ?.configuration?.filter(
                        (f) =>
                          f.seasonId == order?.seasonId &&
                          f.lineID == order?.customData?.lineId,
                      )[0]?.seasonDescription?.[lang] || ""}
                  </OrderDetailText>
                </Box>
                <Box>
                  {getLocalizedString("Linea")}:{" "}
                  <OrderDetailText>
                    {configuration
                      ?.filter?.((f) => f.typeId == order?.orderTypeId)?.[0]
                      ?.configuration?.filter(
                        (f) =>
                          f.lineID == order?.customData?.lineId &&
                          f.seasonId == order?.seasonId,
                      )[0]?.lineDescription?.[lang] || ""}
                  </OrderDetailText>
                </Box>
              </Flex>
              <Flex
                flexDirection="column"
                flex="1 0 calc(100% / 4 - 100px)"
                style={{ display: "grid" }}
              >
                <Box>
                  {getLocalizedString("Porta")}:{" "}
                  <OrderDetailText>
                    {customer?.doors.filter((f) => f.code == "M")[0].code} -{" "}
                    {customer?.doors.filter((f) => f.code == "M")[0].name}
                  </OrderDetailText>
                </Box>
                <Box>
                  {getLocalizedString("ORDER_DATE")}:{" "}
                  <OrderDetailText>
                    {order?.customData?.orderDateFe || ""}
                  </OrderDetailText>
                </Box>
                <Box>
                  {getLocalizedString("Stato")}:{" "}
                  <OrderDetailText
                    style={{
                      textTransform: "uppercase",
                      color: orderColor,
                      fontSize: 16,
                      marginLeft: 2,
                    }}
                  >
                    {getLocalizedString(
                      "STATUS_" + order?.status?.toUpperCase(),
                    )}
                  </OrderDetailText>
                </Box>
              </Flex>
              {order?.customData?.ponumber && (
                <Flex
                  flexDirection="column"
                  flex="1 0 100px"
                  style={{ display: "grid" }}
                >
                  <Box>
                    <span style={{ marginRight: "8px" }}>
                      {getLocalizedString("PO")}
                      {": "}
                      <OrderDetailText>
                        {order?.customData?.ponumber || "-"}
                      </OrderDetailText>
                    </span>
                  </Box>
                </Flex>
              )}
            </Flex>
            {order?.note?.it && (
              <Flex>
                <Flex marginRight={".5em"}>
                  {getLocalizedString("ORDER_NOTES")}:
                </Flex>
                <Box
                  sx={{
                    fontWeight: "bold",
                    fontSize: "0.8em",
                    paddingTop: ".3em",
                    width: "fit-content",
                  }}
                >
                  {order?.note?.it || ""}
                </Box>
              </Flex>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

export default OrderData;
